import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StandaloneThemeToggle } from "@/components/StandaloneThemeToggle";

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/20 via-background to-muted/30 dark:from-background dark:via-background dark:to-background flex flex-col relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl animate-pulse duration-slow"></div>
        <div className="absolute bottom-32 right-20 w-48 h-48 bg-primary/8 rounded-full blur-2xl animate-pulse duration-slow delay-1000"></div>
      </div>

      {/* Navigation */}
      <header className="bg-card/85 backdrop-blur-xl border-b border-border shadow-lg sticky top-0 z-50 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-muted/30 via-card/20 to-muted/30"></div>
        <div className="container mx-auto flex h-20 items-center justify-between px-6 max-w-7xl relative z-10">
          <Link to="/" className="flex items-center gap-3 group transition-all duration-300">
            <div className="relative p-2 rounded-xl bg-card/40 backdrop-blur-sm border border-border group-hover:bg-card/60 group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
              <img
                src="/logo.png"
                alt="Gathio Logo"
                width={32}
                height={32}
                className="h-8 w-8 transition-all duration-300"
              />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent tracking-tight">
              gathio
            </span>
          </Link>

          <nav className="flex items-center gap-3">
            <StandaloneThemeToggle />
            <a
              href="https://admin.gathio.com/auth"
              className="text-muted-foreground hover:text-foreground px-5 py-2.5 rounded-xl transition-all duration-300 font-medium text-sm hover:bg-card/50 hover:backdrop-blur-sm hover:scale-105 border border-transparent hover:border-border"
            >
              Login
            </a>
            <Button
              asChild
              className="px-6 py-2.5 font-medium text-sm rounded-xl"
            >
              <a href="https://admin.gathio.com/auth?mode=signup">Sign Up</a>
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-20 relative z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-card/10 via-transparent to-muted/20 pointer-events-none dark:from-transparent dark:via-transparent dark:to-transparent"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Hero Content */}
          <div className="mb-16">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-card/70 backdrop-blur-md border border-primary/20 rounded-full text-sm font-medium text-primary mb-8 hover:bg-card/90 hover:border-primary/30 transition-all duration-300 animate-in fade-in duration-700 shadow-lg hover:shadow-xl hover:scale-105 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-primary/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
              <span className="w-2 h-2 bg-primary rounded-full animate-pulse relative z-10"></span>
              <span className="relative z-10">Trusted by 10,000+ teams worldwide</span>
            </div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-foreground mb-8 leading-[0.9] tracking-tight animate-in fade-in duration-1000 delay-300">
              Transform Your
              <span className="block bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent relative animate-in fade-in duration-1000 delay-500">
                Team Performance
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-blue-400 to-indigo-400 dark:from-blue-300 dark:to-indigo-300 rounded-full animate-in fade-in duration-1000 delay-700"></div>
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed font-light animate-in fade-in duration-1000 delay-700">
              A comprehensive platform for leaders to conduct effective 1-on-1 sessions,
              track team performance, and improve talent retention through data-driven insights.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-20 animate-in fade-in duration-1000 delay-1000">
            <Button
              size="lg"
              asChild
              className="px-10 py-5 h-auto font-semibold text-lg rounded-2xl group"
            >
              <a href="https://admin.gathio.com/auth?mode=signup">
                Get Started Free
                <span className="ml-2 transition-transform group-hover:translate-x-1">→</span>
              </a>
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-2 border-border text-foreground bg-card/60 backdrop-blur-sm px-10 py-5 h-auto font-semibold text-lg transition-all duration-300 rounded-2xl hover:bg-muted/80 hover:border-border-strong hover:scale-105 hover:shadow-lg"
            >
              <a href="https://admin.gathio.com/auth">
                Sign In
              </a>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 pt-12 animate-in fade-in duration-1000 delay-1400">
            <div className="bg-card/70 backdrop-blur-md border border-border rounded-2xl p-8 text-center group hover:bg-card/90 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-primary/10 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent mb-2 transition-all duration-300 group-hover:scale-110">10,000+</div>
                <div className="text-base text-muted-foreground font-medium">Active Teams</div>
              </div>
            </div>
            <div className="bg-card/70 backdrop-blur-md border border-border rounded-2xl p-8 text-center group hover:bg-card/90 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-success/10 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-success/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 dark:from-emerald-400 dark:to-teal-400 bg-clip-text text-transparent mb-2 transition-all duration-300 group-hover:scale-110">98%</div>
                <div className="text-base text-muted-foreground font-medium">Satisfaction Rate</div>
              </div>
            </div>
            <div className="bg-card/70 backdrop-blur-md border border-border rounded-2xl p-8 text-center group hover:bg-card/90 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-warning/10 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-warning/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 dark:from-amber-400 dark:to-orange-400 bg-clip-text text-transparent mb-2 transition-all duration-300 group-hover:scale-110">45%</div>
                <div className="text-base text-muted-foreground font-medium">Better Retention</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}